import React, { createContext, useContext, useLayoutEffect, useState, useCallback } from 'react';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    // Initialize from localStorage synchronously
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'dark';
    }
    return 'dark';
  });

  const applyTheme = useCallback((newTheme) => {
    // Remove both classes first
    document.documentElement.classList.remove('light', 'dark');
    document.body.classList.remove('light', 'dark');
    
    // Add the new theme class
    document.documentElement.classList.add(newTheme);
    document.body.classList.add(newTheme);
    
    // Update CSS variables for additional customization
    if (newTheme === 'light') {
      document.documentElement.style.setProperty('--bg-primary', '#faf8ff');
      document.documentElement.style.setProperty('--text-primary', '#1a1025');
      document.body.style.background = 'linear-gradient(135deg, #faf8ff 0%, #f3f0ff 50%, #ede9fe 100%)';
      document.body.style.color = '#1a1025';
    } else {
      document.documentElement.style.setProperty('--bg-primary', '#0a0412');
      document.documentElement.style.setProperty('--text-primary', '#ffffff');
      document.body.style.background = 'linear-gradient(135deg, #0a0412 0%, #130a1f 50%, #1a0a2e 100%)';
      document.body.style.color = '#ffffff';
    }
  }, []);

  // Use layoutEffect to apply theme before paint
  useLayoutEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
